// Build a draft ADP claim from a patient record + device details. This is the
// paperwork Prestin fills so the front desk doesn't re-key it: the application
// fields for form 014-3224-67 (post-March-2026), the funding split, the 5-year
// clock, and the confirmed subset of the 16-field invoice .txt. It stops short
// of anything a person must own: the authorizer/applicant signatures, the
// mandatory attestation, and the GO Secure/MCEDT upload are manual actions, not
// auto-performed.

import { computeFunding } from "./funding";
import { fundingPeriodClock } from "./eligibility";
import {
  money,
  type AdpClaim,
  type AdpClaimInput,
  type Cell,
  type DeviceAid,
  type InvoiceTxtRecord,
} from "./types";
import type { ParsedInvoice } from "../ingest/types";

// OHIP health number: 10 digits, optionally a 2-letter version code.
function normalizeHealthNumber(raw: string): { digits: string; ok: boolean } {
  const cleaned = raw.replace(/[^0-9A-Za-z]/g, "");
  const digits = cleaned.replace(/[^0-9]/g, "");
  return { digits, ok: /^\d{10}([A-Za-z]{2})?$/.test(cleaned) };
}

function expectedAidCount(t: AdpClaimInput["fittingType"]): number {
  return t === "binaural" ? 2 : 1;
}

// The steps that stay a person's, on every claim, regardless of how complete
// the drafted data is. Exported so any earlier-stage preview (the wedge's
// receiving-time ADP draft) can show the same list without a claim's full
// patient/authorizer/vendor identity yet on hand.
export const ADP_MANUAL_ACTIONS: string[] = [
  "Authorizer signs and dates the application (prescriber signature no longer required as of 2026-03-02).",
  "Applicant signs the application.",
  "Attestation Sheet 014-ON00881E signed by BOTH vendor and applicant (mandatory since 2026-03-02).",
  "Export XML + invoice .txt and upload via GO Secure / MCEDT (edt.health.gov.on.ca) — the one step Prestin cannot perform for you.",
];

export function buildAdpClaim(input: AdpClaimInput, asOf: string): AdpClaim {
  const { fittingType, aids, applicant, authorizer, vendor } = input;
  const funding = computeFunding(fittingType, aids);
  const eligibility = fundingPeriodClock(fittingType, input.priorFundingDate, asOf);
  const hn = normalizeHealthNumber(applicant.healthNumber);

  const application: Cell[] = [
    { label: "Applicant name", value: applicant.name || null, required: true, source: "record" },
    { label: "Health number", value: applicant.healthNumber || null, required: true, source: "record" },
    { label: "Date of birth", value: applicant.dateOfBirth || null, required: true, source: "record" },
    { label: "Address", value: applicant.address || null, required: true, source: "record" },
    { label: "Authorizer name", value: authorizer.name || null, required: true, source: "record" },
    { label: "Authorizer registration (CASLPO)", value: authorizer.registrationNumber || null, required: true, source: "record" },
    { label: "Authorizer category", value: authorizer.category || null, required: true, source: "record" },
    { label: "Authorizer signature + date", value: null, required: true, source: "manual" },
    { label: "Vendor name", value: vendor.name || null, required: true, source: "record" },
    { label: "ADP vendor number", value: vendor.adpVendorNumber || null, required: true, source: "record" },
    { label: "Vendor invoice number", value: vendor.invoiceNumber || null, required: true, source: "record" },
    { label: "Invoice date", value: vendor.invoiceDate || null, required: true, source: "record" },
    { label: "Delivery date", value: vendor.deliveryDate || null, required: true, source: "record" },
    ...aids.flatMap((a, i): Cell[] => {
      const n = aids.length > 1 ? ` (${a.ear ?? `aid ${i + 1}`})` : "";
      return [
        { label: `Device make/model${n}`, value: `${a.make} ${a.model}`.trim() || null, required: true, source: "record" },
        { label: `Serial${n}`, value: a.serial || null, required: true, source: "record" },
        { label: `Device cost${n}`, value: money(a.costCents), required: true, source: "record" },
      ];
    }),
    { label: "ADP contribution", value: money(funding.contributionCents), required: true, source: "computed" },
    { label: "Client portion (collected by vendor)", value: money(funding.clientPaysCents), required: true, source: "computed" },
    { label: "Applicant signature", value: null, required: true, source: "manual" },
  ];

  const invoiceTxt: InvoiceTxtRecord = {
    totalFields: 16,
    confirmed: [
      { label: "ADP Vendor Number", value: vendor.adpVendorNumber || null },
      { label: "ADP Claim Number", value: null }, // assigned on/with the application
      { label: "Client Health Number", value: hn.digits || null },
      { label: "Vendor Invoice Number", value: vendor.invoiceNumber || null },
      { label: "Invoice Date", value: vendor.invoiceDate || null },
      { label: "Delivery Date", value: vendor.deliveryDate || null },
      { label: "Serial Number", value: aids.map(a => a.serial).filter(Boolean).join(", ") || null },
    ],
    pendingFieldNote:
      "7 of 16 fields confirmed from the eSubmission Technical Specification; the remaining 9 " +
      "need the published E-Claim field list before the .txt can be serialized (research open item).",
  };

  const blockers: string[] = [];
  if (!hn.ok) blockers.push(`Health number "${applicant.healthNumber}" is not a valid OHIP number (10 digits, optional 2-letter version).`);
  if (!vendor.adpVendorNumber) blockers.push("Missing ADP vendor number — the clinic's registered-vendor number is required.");
  const noSerial = aids.filter(a => !a.serial.trim());
  if (noSerial.length) blockers.push(`${noSerial.length} device(s) missing a serial number — a claim cannot go out without every serial.`);
  if (aids.length !== expectedAidCount(fittingType)) {
    blockers.push(`Fitting is ${fittingType} but ${aids.length} device(s) were supplied (expected ${expectedAidCount(fittingType)}).`);
  }
  if (!eligibility.eligible && eligibility.eligibleFrom) {
    blockers.push(`Inside the ${eligibility.fundingPeriodYears}-year funding period — not eligible until ${eligibility.eligibleFrom} (${eligibility.daysRemaining} days).`);
  }

  return {
    form: "014-3224-67",
    formVersion: "2026-03-02",
    attestationForm: "014-ON00881E",
    funding,
    eligibility,
    application,
    invoiceTxt,
    blockers,
    manualActions: ADP_MANUAL_ACTIONS,
  };
}

// Lift the device details the wedge already read off the manufacturer invoice
// into ADP aid entries. One aid per invoice line; a qty > 1 line is flagged
// rather than silently split, since one serial cannot cover two devices. The
// patient/authorizer/vendor identity fields come from the patient record, not
// the invoice, so the caller supplies those separately.
export function aidsFromParsedInvoice(inv: ParsedInvoice): DeviceAid[] {
  return inv.items.map((it): DeviceAid => ({
    make: inv.maker.value,
    model: it.model.value,
    serial: it.serial.value ?? "",
    costCents: Math.round(it.unitPrice.value * 100),
    qtyNote: it.qty.value > 1 ? `invoice line billed qty ${it.qty.value}; confirm one serial per device` : undefined,
  }));
}
